import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Button from '../../components/Button/Button';
import { getHmdState, updateHmdState } from '../../services/hmd';
import displayModeObjectives from './HmdDisplayModeObjectives.json';
import displayActionObjectives from './HmdActionModeObjectives.json';
import HmdEventsTable from './HmdEventsTable';
import { HmdDisplays } from './HmdDisplays';

import './HmdPage.css';

const validateTimestampInMs = (timestamp) => moment(timestamp, 'X', true).isValid();

// TODO: Update to use HTML TABLES IN THE FUTURE - this is getting messy as we add to it

export default function HmdPage(props) {
  const { webSocketService } = props || {};
  const [thingName, setThingName] = useState('h3po-hmd-1');
  const [updateStartCount, setUpdateStartCount] = useState(0);
  const [petStartTimestamp, setPetStartTimestamp] = useState(Date.now());
  const [updateLog, setUpdateLog] = useState({ responses: [] });
  const [states, setStates] = useState({ desired: { display: 'DEFAULT', action: 'SWITCH-MODE' } });
  const [lastState, setLastState] = useState();
  const [desiredDisplayState, setDesiredDisplayState] = useState({
    line1: 'Welcome',
    line2: 'to your',
    line3: 'HMD!',
    line1Color: '',
    line2Color: '',
    line3Color: '',
  });
  const { desired: desiredState = {}, reported: reportedState = {} } = states || {};
  const {
    display: desiredDisplay = 'DEFAULT',
    action: desiredAction = 'SWITCH-MODE',
    line1Text: desiredLine1Text = '',
    line2Text: desiredLine2Text = '',
    line3Text: desiredLine3Text = '',
    properties: {
      maxMetRate: desiredMaxMetRate = 5015,
      screenRotation: desiredScreenRotation = 270,
      maxStateListLength: desiredMaxStateListLength = 300,
      defaultDisplay = 'MET-PET',
      returnToDefaultTime = 120,
    } = {},
  } = desiredState;

  const formatTimerDiff = (startTimestamp) => {
    const duration = Date.now() - startTimestamp;
    let minutes = Math.floor((duration / (1000 * 60)) % 60);
    let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    hours = hours.toString().padStart(2, '0');
    minutes = minutes.toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  const fromReportedSetDisplay = (reported) => {
    const { properties = {} } = reported;
    const { display_states: displayStates = {} } = properties || {};

    const {
      average_btu_hr: metRate = '',
      met_rate_percent: metPercent = '',
      timer1 = '',
      timer2 = '',
    } = displayStates || {};

    const reportedDisplay = {
      ...HmdDisplays[reported.display],
    };

    if (HmdDisplays[reported.display].line1.length === 0) {
      reportedDisplay.line1 = reported.line1Text;
    }
    if (HmdDisplays[reported.display].line2.length === 0) {
      reportedDisplay.line2 = reported.line2Text;
    }
    if (HmdDisplays[reported.display].line3.length === 0) {
      reportedDisplay.line3 = reported.line3Text;
    }

    switch (reported.display) {
      case 'PET':
        reportedDisplay.line2 = formatTimerDiff(petStartTimestamp);
        break;
      case 'MET-PET':
        reportedDisplay.line1 = `${metPercent}%`;
        reportedDisplay.line2 = formatTimerDiff(petStartTimestamp);
        break;
      case 'BOLT':
        reportedDisplay.line3 += ' TURNS';
        break;
      case 'PGT':
        reportedDisplay.line1 = `PGT: ${reportedDisplay.line1}`;
        break;
      case 'MET-RATE':
        reportedDisplay.line2 = metRate;
        break;
      case 'MET-RATE-PERCENT':
        reportedDisplay.line2 = `${metPercent}%`;
        break;
      case 'TIMER1':
        reportedDisplay.line2 = timer1;
        break;
      case 'TIMER2':
        reportedDisplay.line2 = timer2;
        break;
      default:
        return reportedDisplay;
    }

    return reportedDisplay;
  };

  useEffect(() => {
    getHmdState(thingName).then((response) => {
      const { reported, desired } = response || {};

      setLastState(desired.lastState);

      const petTimestamp = reported?.properties?.petTimestamp;
      if (petTimestamp && validateTimestampInMs(petTimestamp)) {
        setPetStartTimestamp(petTimestamp);
      }
      const reportedDisplay = fromReportedSetDisplay(reported);
      setDesiredDisplayState(reportedDisplay);
    });
  }, []);

  useEffect(() => {
    if (updateStartCount === 0) {
      return;
    }
    updateHmdState(thingName, desiredState).then((response) => {
      const { hmd_updated: reported } = response || {};
      setLastState(reported.lastState);

      const reportedDisplay = fromReportedSetDisplay(reported);

      setDesiredDisplayState(reportedDisplay);
      setUpdateLog({
        ...updateLog,
        error: false,
        responses: [...updateLog.responses, response],
      });
    }).catch((response) => {
      setUpdateLog({
        ...updateLog,
        error: true,
        responses: [...updateLog.responses, response],
      });
    });
  }, [updateStartCount]);

  return (
    <div className="hmd-page">
      <div className="hmd-page-title">HMD</div>
      <div className="hmd-thing-selector">
        <h3>HMD Target Image:</h3>
        <select
          className="thing-name-selector"
          value={thingName}
          onChange={({ target: { value: selectedThingName } }) => setThingName(selectedThingName)}
        >
          {
            ['h3po-hmd-1', 'h3po-hmd-2'].map((currentThingName) => (
              <option value={currentThingName} key={`option-${currentThingName}`}>
                {currentThingName}
              </option>
            ))
          }
        </select>
      </div>
      <h3>Desired</h3>
      <div className="hmd-desired">
        <div className="hmd-desired-state">
          <label htmlFor="hmd-action">
            <div>Action</div>
            <select
              className="hmd-action"
              value={desiredAction}
              onChange={
                ({ target: { value } }) => {
                  if (value === 'RECALL') {
                    setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        ...lastState,
                      },
                    });
                  } else {
                    setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        action: value,
                      },
                    });
                  }
                }
              }
            >
              {
                Object.keys(displayActionObjectives)
                  .map((currentAction) => (
                    <option value={currentAction} key={`option-${currentAction}`}>
                      {currentAction}
                    </option>
                  ))
              }
            </select>
          </label>
          {states.desired.action === 'RESET'
            ? (
              <label htmlFor="hmd-display">
                <div>Display</div>
                <select
                  className="hmd-display"
                  value={desiredDisplay}
                  onChange={({ target: { value } }) => setStates({
                    ...states,
                    desired: {
                      ...desiredState,
                      display: value,
                    },
                  })}
                >
                  {
                    ['TIMER1', 'TIMER2', 'PET'].map((currentDesiredDisplay) => (
                      <option value={currentDesiredDisplay} key={`option-${currentDesiredDisplay}`}>
                        {currentDesiredDisplay}
                      </option>
                    ))
                  }
                </select>
              </label>
            ) : null}
          {states.desired.action === 'SWITCH-MODE'
            ? (
              <div className="hmd-line-text-input-container">
                <label htmlFor="hmd-display">
                  <div>Display</div>
                  <select
                    className="hmd-display"
                    value={desiredDisplay}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        display: value,
                      },
                    })}
                  >
                    {
                      Object.keys(displayModeObjectives)
                        .map((currentDesiredDisplay) => (
                          <option value={currentDesiredDisplay} key={`option-${currentDesiredDisplay}`}>
                            {currentDesiredDisplay}
                          </option>
                        ))
                    }
                  </select>
                </label>
                <label htmlFor="hmd-line-1">
                  <div>Line 1</div>
                  <input
                    id="hmd-line-1"
                    name="hmd-line-1"
                    type="text"
                    value={desiredLine1Text}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        line1Text: value,
                      },
                    })}
                  />
                </label>
                <label htmlFor="hmd-line-2">
                  <div>Line 2</div>
                  <input
                    id="hmd-line-2"
                    name="hmd-line-2"
                    type="text"
                    value={desiredLine2Text}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        line2Text: value,
                      },
                    })}
                  />
                </label>
                <label htmlFor="hmd-line-3">
                  <div>Line 3</div>
                  <input
                    id="hmd-line-3"
                    name="hmd-line-3"
                    type="text"
                    value={desiredLine3Text}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        line3Text: value,
                      },
                    })}
                  />
                </label>
              </div>
            ) : null}
          {states.desired.action === 'SET-DISPLAY-PROPERTIES'
            ? (
              <div className="hmd-property-objectives">
                <label htmlFor="screen-rotation">
                  <div>Screen Rotation (deg)</div>
                  <select
                    className="hmd-screen-rotation"
                    value={desiredScreenRotation}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        properties: {
                          ...desiredState.properties,
                          screenRotation: value,
                        },
                      },
                    })}
                  >
                    {[0, 90, 180, 270].map((rotation) => (
                      <option value={rotation} key={`option-${rotation}`}>
                        {`${rotation} Degrees`}
                      </option>
                    ))}
                  </select>
                </label>
                <label htmlFor="max-met-rate">
                  <div>Max Met Rate (BTU)</div>
                  <input
                    id="max-met-rate-hmd-property"
                    name="max-met-rate-hmd-property"
                    type="text"
                    value={desiredMaxMetRate}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        properties: {
                          ...desiredState.properties,
                          maxMetRate: value,
                        },
                      },
                    })}
                  />
                </label>
                <label htmlFor="rolling-met-rate">
                  <div>Rolling Met Rate</div>
                  <div>(# of list entries)</div>
                  <input
                    id="rolling-met-rate-hmd-property"
                    name="rolling-met-rate-hmd-property"
                    type="text"
                    value={desiredMaxStateListLength}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        properties: {
                          ...desiredState.properties,
                          maxStateListLength: value,
                        },
                      },
                    })}
                  />
                </label>
                <label htmlFor="default-display">
                  <div>Default Display</div>
                  <select
                    className="hmd-default-display"
                    value={defaultDisplay}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        properties: {
                          ...desiredState.properties,
                          defaultDisplay: value,
                        },
                      },
                    })}
                  >
                    {
                      Object.keys(displayModeObjectives)
                        .map((currentDesiredDisplay) => (
                          <option value={currentDesiredDisplay} key={`option-${currentDesiredDisplay}`}>
                            {currentDesiredDisplay}
                          </option>
                        ))
                    }
                  </select>
                </label>
                <label htmlFor="return-to-default-display-time">
                  <div>Return to default display time (seconds)</div>
                  <input
                    id="return-to-default-display-time"
                    name="return-to-default-display-time"
                    type="text"
                    value={returnToDefaultTime}
                    onChange={({ target: { value } }) => setStates({
                      ...states,
                      desired: {
                        ...desiredState,
                        properties: {
                          ...desiredState.properties,
                          returnToDefaultTime: value,
                        },
                      },
                    })}
                  />
                </label>
              </div>
            ) : null}

          <Button onClick={() => setUpdateStartCount(updateStartCount + 1)} label="Save" />
          {updateLog.error ? <div className="hmd-error">Error</div> : null}
        </div>
        <div className="display-docs">
          {(states.desired.action === 'SWITCH-MODE' || states.desired.action === 'RESET')
            ? (
              <div className="hmd-display-docs">
                <div className="hmd-docs-display">
                  <img src={`${process.env.PUBLIC_URL}/hmd-display-examples/${desiredDisplay}.png`} alt={desiredDisplay} />
                </div>
                <div className="hmd-text">
                  <div className="hmd-docs-display-objective">
                    <h4>
                      <span>Desired Display: </span>
                      {desiredDisplay}
                    </h4>
                    <div>{displayModeObjectives[desiredDisplay]}</div>
                  </div>
                  <div className="hmd-desired-action">
                    <h4>
                      <span>Desired Action: </span>
                      {desiredAction}
                    </h4>
                    <div>{displayActionObjectives[desiredAction]}</div>
                  </div>
                </div>
              </div>
            )
            : (
              <div className="hmd-desired-action">
                <h4>
                  <span>Desired Action: </span>
                  {desiredAction}
                </h4>
                <div>{displayActionObjectives[desiredAction]}</div>
              </div>
            )}
        </div>
      </div>

      <h3>Reported</h3>
      <div className="hmd-reported">
        <div className="hmd-reported-state">
          <label htmlFor="hmd-action-reported">
            <div>Action</div>
            <select
              className="hmd-action"
              value={reportedState.action}
              disabled
            >
              {
                Object.keys(displayActionObjectives)
                  .map((currentReportedAction) => (
                    <option value={currentReportedAction} key={`reported-option-${currentReportedAction}`}>
                      {currentReportedAction}
                    </option>
                  ))
              }
            </select>
          </label>
          <label htmlFor="hmd-display-reported">
            <div>Display</div>
            <select
              className="hmd-display"
              value={reportedState.display}
              disabled
            >
              {
                Object.keys(displayModeObjectives)
                  .map((currentReportedDisplay) => (
                    <option value={currentReportedDisplay} key={`reported-option-${currentReportedDisplay}`}>
                      {currentReportedDisplay}
                    </option>
                  ))
              }
            </select>
          </label>
          <label htmlFor="hmd-line-1-reported">
            <div>Line 1</div>
            <input id="hmd-line-1-reported" name="hmd-line-1-reported" type="text" value={reportedState.line1Text || ''} disabled />
          </label>
          <label htmlFor="hmd-line-2-reported">
            <div>Line 2</div>
            <input id="hmd-line-2-reported" name="hmd-line-2-reported" type="text" value={reportedState.line2Text || ''} disabled />
          </label>
          <label htmlFor="hmd-line-3-reported">
            <div>Line 3</div>
            <input id="hmd-line-3-reported" name="hmd-line-3-reported" type="text" value={reportedState.line3Text || ''} disabled />
          </label>
          <label htmlFor="hmd-display-rotation-reported">
            <div>Display Rotation (Degrees)</div>
            <input
              id="hmd-display-rotation-reported"
              name="hmd-display-rotation-reported"
              type="text"
              value={reportedState.properties ? reportedState.properties.screenRotation : 270}
              disabled
            />
          </label>
          <label htmlFor="hmd-max-met-rate-reported">
            <div>Max Met Rate in BTU</div>
            <input
              id="hmd-max-met-rate-reported"
              name="hmd-max-met-rate-reported"
              type="text"
              value={reportedState.properties ? reportedState.properties.maxMetRate : 5015}
              disabled
            />
          </label>
          <label htmlFor="hmd-rolling-met-rate-reported">
            <div>Rolling Met Rate</div>
            <div>(# of list entries)</div>
            <input
              id="hmd-rolling-met-rate-reported"
              name="hmd-rolling-met-rate-reported"
              type="text"
              value={reportedState.properties ? reportedState.properties.maxStateListLength : 300}
              disabled
            />
          </label>
          <Button
            onClick={() => setUpdateLog({
              ...updateLog,
              error: false,
              reloadCounter: (updateLog.reloadCounter || 0) + 1,
            })}
            label="Refresh"
          />
        </div>
        <div className="hmd-reported-display">
          <div className="container">
            <div
              className="line1-reported"
              style={
                { color: desiredDisplayState && desiredDisplayState.line1Color ? desiredDisplayState.line1Color : '' }
              }
            >
              {desiredDisplayState && desiredDisplayState.line1 ? desiredDisplayState.line1 : ''}
            </div>
            <div
              className="line2-reported"
              style={
                { color: desiredDisplayState && desiredDisplayState.line2Color ? desiredDisplayState.line2Color : '' }
              }
            >
              {desiredDisplayState && desiredDisplayState.line2 ? desiredDisplayState.line2 : ''}
            </div>
            <div
              className="line3-reported"
              style={
                { color: desiredDisplayState && desiredDisplayState.line3Color ? desiredDisplayState.line3Color : '' }
              }
            >
              {desiredDisplayState && desiredDisplayState.line3 ? desiredDisplayState.line3 : ''}
            </div>
          </div>
          <div className="reported-display-description">Reported HMD State</div>
        </div>
      </div>

      <h3>Events</h3>
      <HmdEventsTable webSocketService={webSocketService} />
    </div>
  );
}
